import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import IconButton from "@mui/material/IconButton";
import Swal from "sweetalert2";
import { IoMdAdd } from "react-icons/io";
import Hexapi from "../usefulcomps/HexAPI/Hexapi";
import { TimeFormatterV2 } from "../usefulcomps/Timeformatter/TimeFormatter";
import axios from "axios";

// Payment methods matching the PHP template exactly
const PAYMENT_METHODS = ["Cash", "Cheque", "RTGS / NEFT", "UPI", "Other"];

function MoneyReceiptForm(props) {
  const [showmodal, setshowmodal] = useState(false);
  const [modalLoading, setModalLoading] = useState(true);

  // ── Data from SP ─────────────────────────────────────────
  const [RemainingAmount, setRemainingAmount] = useState(0);
  const [RevisionNo, setRevisionNo] = useState(0);

  // ── Form fields ──────────────────────────────────────────
  const today = new Date().toISOString().split("T")[0];
  const [Amount, setAmount] = useState(0);
  const [ReceivedOn, setReceivedOn] = useState(today);
  const [Purpose, setPurpose] = useState("");

  // Payment method
  const [PaymentMethod, setPaymentMethod] = useState("Cash");

  // Cash
  const [CashNote, setCashNote] = useState("");

  // Cheque
  const [ChequeNo, setChequeNo] = useState("");
  const [ChequeBank, setChequeBank] = useState("");
  const [ChequeDate, setChequeDate] = useState(today);

  // RTGS / NEFT
  const [UtrNumber, setUtrNumber] = useState("");

  // UPI
  const [UpiId, setUpiId] = useState("");
  const [UpiTxn, setUpiTxn] = useState("");

  // Other
  const [OtherDetails, setOtherDetails] = useState("");

  // ── Fetch enquiry data ────────────────────────────────────
  const getdata = async () => {
    setModalLoading(true);
    const obj = {
      query: `[dbo].[CRM_Proc_MoneyReceipt_GetData] @agentID='{0}', @EnquiryID='{1}'`,
      queryArr: [`${props.AgentID}`, `${props.enquiryID}`],
    };
    try {
      const resp = await Hexapi(obj);
      setRevisionNo("0" + resp[""][0]["revision"]);
      setAmount(resp[""][0]["amount"]);
      setRemainingAmount(resp[""][0]["remainingamount"]);
    } catch (err) {
      console.error("Error fetching row details:", err);
    } finally {
      setModalLoading(false);
    }
  };

  // ── Validation ────────────────────────────────────────────
  const validate = () => {
    if (!Amount || Amount <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Required",
        text: "Please enter a valid Amount.",
      });
      return false;
    }
    if (!ReceivedOn) {
      Swal.fire({
        icon: "warning",
        title: "Required",
        text: "Please select a Received On date.",
      });
      return false;
    }
    if (PaymentMethod === "Cheque" && !ChequeNo) {
      Swal.fire({
        icon: "warning",
        title: "Required",
        text: "Please enter Cheque Number.",
      });
      return false;
    }
    if (PaymentMethod === "Cheque" && !ChequeBank) {
      Swal.fire({
        icon: "warning",
        title: "Required",
        text: "Please enter Bank & Branch.",
      });
      return false;
    }
    if (PaymentMethod === "RTGS / NEFT" && !UtrNumber) {
      Swal.fire({
        icon: "warning",
        title: "Required",
        text: "Please enter UTR Number.",
      });
      return false;
    }
    if (PaymentMethod === "UPI" && !UpiId) {
      Swal.fire({
        icon: "warning",
        title: "Required",
        text: "Please enter UPI ID.",
      });
      return false;
    }
    return true;
  };

  // ── Format date for display (dd.MM.yyyy) ─────────────────
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return (
      String(d.getDate()).padStart(2, "0") +
      "." +
      String(d.getMonth() + 1).padStart(2, "0") +
      "." +
      d.getFullYear()
    );
  };

  // ── Submit ────────────────────────────────────────────────
  const submit = async () => {
    if (!validate()) return;
    setModalLoading(true);

    // Build SP query — store all payment fields in DB
    const obj = {
      query: `[dbo].[CRM_Proc_MoneyReceipt_Submit] 
        @agentID='{0}', 
        @EnquiryID='{1}', 
        @Amount='{2}', 
        @ReceivedOn='{3}',
        @PaymentMethod='{4}',
        @CashNote='{5}',
        @ChequeNo='{6}',
        @ChequeBank='{7}',
        @ChequeDate='{8}',
        @UtrNumber='{9}',
        @UpiId='{10}',
        @UpiTxn='{11}',
        @OtherDetails='{12}',
        @Purpose='{13}'`,
      queryArr: [
        `${props.AgentID}`,
        `${props.enquiryID}`,
        `${Amount}`,
        `${TimeFormatterV2(ReceivedOn, "fulldate", "YYYY-MM-DD HH:mm:ss")}`,
        `${PaymentMethod}`,
        `${CashNote}`,
        `${ChequeNo}`,
        `${ChequeBank}`,
        `${ChequeDate ? TimeFormatterV2(ChequeDate, "fulldate", "YYYY-MM-DD HH:mm:ss") : ""}`,
        `${UtrNumber}`,
        `${UpiId}`,
        `${UpiTxn}`,
        `${OtherDetails}`,
        `${Purpose}`,
      ],
    };

    try {
      const resp = await Hexapi(obj);

      if (resp?.[""]?.[0]?.command === 1) {
        const res = await fetch(`${process.env.PUBLIC_URL}/Apiinfo.json`);
        const data = await res.json();
        if (!data?.apipath) throw new Error("API Path missing");

        // ── POST fields matching SendMoneyReceipt.php exactly ──
        const postData = {
          // Document meta
          EnquiryID: props.enquiryID,
          Today: new Date().toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          }),
          OpportunityReferenceNumber:
            resp[""][0]["opportunityreferencenumber"] ?? "",
          Revision: RevisionNo,
          EmailID: resp[""][0]["emailid"],
          // Receipt data
          ReceivedFrom: resp[""][0]["customername"] ?? "",
          Amount: Amount,
          PaymentMethod: PaymentMethod,
          Purpose: Purpose,
          Panel: resp[""][0]["panel"],
          ContactNumber: resp[""][0]["contactnumber"],
          CustomerName: resp[""][0]["customername"],
          CompanyName: resp[""][0]["companyname"],
          Rating: resp[""][0]["rating"],
          // Cash
          CashNote: CashNote,

          // Cheque
          ChequeNo: ChequeNo,
          ChequeBank: ChequeBank,
          ChequeDate: ChequeDate ? formatDateDisplay(ChequeDate) : "",

          // RTGS / NEFT
          UtrNumber: UtrNumber,

          // UPI
          UpiId: UpiId,
          UpiTxn: UpiTxn,

          // Other
          OtherDetails: OtherDetails,
        };

        const url = `${data.apipath}SendMoneyReceipt.php`;
        await axios.post(url, postData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        await Swal.fire({
          icon: "success",
          title: "Money Receipt Sent",
          text: "Money receipt email has been sent successfully.",
          confirmButtonText: "OK",
        });

        setshowmodal(false);
        props.RefreshLogs();
      }
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: "Failed",
        text:
          error?.response?.data?.message ||
          error.message ||
          "Something went wrong while sending the money receipt.",
      });
    } finally {
      setModalLoading(false);
    }
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <>
      <button
        disabled={props.disabled}
        onClick={() => {
          setshowmodal(true);
          getdata();
        }}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          cursor: props.disabled ? "not-allowed" : "pointer",
        }}
      >
        <IoMdAdd
          style={{ color: "grey", fontSize: "25px" }}
          title="Send Money Receipt"
        />
      </button>

      <Modal
        show={showmodal}
        onHide={() => setshowmodal(false)}
        dialogClassName="custom-modall-quote"
        size="lg"
        scrollable
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton style={{ backgroundColor: "#f5f9ff" }}>
          <div className="w-100">
            <div>
              <strong>Money Receipt Form</strong>
            </div>
            <div className="d-flex gap-4 mt-2">
              <span>
                <strong>EnquiryID:</strong> {props.enquiryID}
              </span>
              <span>
                <strong>Customer:</strong> {props.customerName}
              </span>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          {!modalLoading ? (
            <form className="p-3 mx-auto" style={{ maxWidth: "720px" }}>
              <div className="row g-3">
                {/* ── Revision (read-only) ── */}
                <div className="col-2">
                  <label className="form-label">Revision</label>
                  <input className="form-control" value={RevisionNo} disabled />
                </div>

                {/* ── Amount ── */}
                <div className="col-md-5">
                  <label className="form-label">
                    Amount <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="₹"
                    value={Amount}
                    max={RemainingAmount}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      setAmount(v <= RemainingAmount ? v : RemainingAmount);
                    }}
                    required
                  />
                </div>

                {/* ── Remaining Amount (read-only) ── */}
                <div className="col-md-5">
                  <label className="form-label">Remaining Amount</label>
                  <input
                    type="number"
                    className="form-control"
                    value={RemainingAmount}
                    disabled
                  />
                </div>

                {/* ── Received On ── */}
                <div className="col-md-6">
                  <label className="form-label">
                    Received On <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={ReceivedOn}
                    onChange={(e) => setReceivedOn(e.target.value)}
                    required
                  />
                </div>

                {/* ── Purpose of Payment ── */}
                <div className="col-md-6">
                  <label className="form-label">Purpose of Payment</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. 30% advance for DG Set supply"
                    value={Purpose}
                    onChange={(e) => setPurpose(e.target.value)}
                  />
                </div>

                {/* ── Payment Method ── */}
                <div className="col-12">
                  <label className="form-label">
                    Payment Method <span className="text-danger">*</span>
                  </label>
                  <div className="d-flex flex-wrap gap-2">
                    {PAYMENT_METHODS.map((m) => (
                      <label
                        key={m}
                        className="d-flex align-items-center gap-1 px-3 py-2 rounded border"
                        style={{
                          cursor: "pointer",
                          fontWeight: 500,
                          fontSize: "13px",
                          backgroundColor:
                            PaymentMethod === m ? "#e8f0fe" : "#fff",
                          borderColor:
                            PaymentMethod === m ? "#1a4b8c" : "#dee2e6",
                          color: PaymentMethod === m ? "#1a4b8c" : "#1a1a1a",
                          transition: "all 0.15s",
                        }}
                      >
                        <input
                          type="radio"
                          name="paymentMethod"
                          value={m}
                          checked={PaymentMethod === m}
                          onChange={() => setPaymentMethod(m)}
                          style={{ accentColor: "#1a4b8c" }}
                        />
                        {m}
                      </label>
                    ))}
                  </div>
                </div>

                {/* ── Cash fields ── */}
                {PaymentMethod === "Cash" && (
                  <div className="col-md-8">
                    <label className="form-label">Cash Note (optional)</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Received in cash at office"
                      value={CashNote}
                      onChange={(e) => setCashNote(e.target.value)}
                    />
                  </div>
                )}

                {/* ── Cheque fields ── */}
                {PaymentMethod === "Cheque" && (
                  <>
                    <div className="col-md-4">
                      <label className="form-label">
                        Cheque Number <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={ChequeNo}
                        onChange={(e) => setChequeNo(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-md-5">
                      <label className="form-label">
                        Bank &amp; Branch <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={ChequeBank}
                        onChange={(e) => setChequeBank(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Cheque Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={ChequeDate}
                        onChange={(e) => setChequeDate(e.target.value)}
                      />
                    </div>
                  </>
                )}

                {/* ── RTGS / NEFT fields ── */}
                {PaymentMethod === "RTGS / NEFT" && (
                  <div className="col-md-8">
                    <label className="form-label">
                      UTR Number <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="UTR / Transaction reference"
                      value={UtrNumber}
                      onChange={(e) => setUtrNumber(e.target.value)}
                      required
                    />
                  </div>
                )}

                {/* ── UPI fields ── */}
                {PaymentMethod === "UPI" && (
                  <>
                    <div className="col-md-5">
                      <label className="form-label">
                        UPI ID <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="example@upi"
                        value={UpiId}
                        onChange={(e) => setUpiId(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-md-5">
                      <label className="form-label">UPI Transaction ID</label>
                      <input
                        type="text"
                        className="form-control"
                        value={UpiTxn}
                        onChange={(e) => setUpiTxn(e.target.value)}
                      />
                    </div>
                  </>
                )}

                {/* ── Other fields ── */}
                {PaymentMethod === "Other" && (
                  <div className="col-md-8">
                    <label className="form-label">Details</label>
                    <input
                      type="text"
                      className="form-control"
                      value={OtherDetails}
                      onChange={(e) => setOtherDetails(e.target.value)}
                    />
                  </div>
                )}

                {/* ── Submit ── */}
                <div className="col-12 text-center">
                  <IconButton
                    sx={{
                      mt: 3,
                      px: 4,
                      py: 1.2,
                      borderRadius: "8px",
                      backgroundColor: "#000",
                      border: "1px solid #000",
                      color: "#fff",
                      "&:hover": { backgroundColor: "#fff", color: "#000" },
                      fontSize: "16px",
                      fontWeight: 600,
                    }}
                    onClick={submit}
                  >
                    Submit
                  </IconButton>
                </div>
              </div>
            </form>
          ) : (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: "200px" }}
            >
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}

export default MoneyReceiptForm;
